import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import posthog from 'posthog-js';
import { filter } from 'rxjs/operators';
import { Hilfe } from '../../components/hilfe/Hilfe';
import { POSTHOG_CONFIG, PosthogConfig } from '../../kiene-core.config';
import { KieneSession } from '../local-storage/kiene-session';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { getLegacyWebViewPlatform } from '../../utils/legacy-webview-shell';

@Injectable({ providedIn: 'root' })
export class PosthogService {
    private lastDistinctId: string | null = null;
    private started = false;

    constructor(
        private ngZone: NgZone,
        private router: Router,
        private localStorageService: LocalStorageService,
        @Optional() @Inject(POSTHOG_CONFIG) private config: PosthogConfig | null,
    ) {
        this.init();
    }

    capture(event: string, properties?: Record<string, string | number | boolean>): void {
        if (!this.started) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            posthog.capture(event, this.withApp(properties));
        });
    }

    captureException(error: unknown): void {
        if (!this.started || !error) {
            return;
        }
        const unwrapped = this.unwrapError(error);
        if (unwrapped instanceof HttpErrorResponse) {
            return;
        }
        if (this.shouldIgnoreMessage(unwrapped.message)) {
            return;
        }
        this.sendException(unwrapped, { source: 'error_handler' });
    }

    captureHttpError(response: HttpErrorResponse, method: string): void {
        if (!this.started || !response) {
            return;
        }
        const status = response.status;
        if (status !== 0 && status < 500) {
            return;
        }
        const apiPath = this.sanitizeUrl(response.url || '');
        if (apiPath.indexOf('exceptions/create') >= 0) {
            return;
        }
        const message =
            status === 0
                ? 'HTTP network error ' + method + ' ' + apiPath
                : 'HTTP ' + status + ' ' + method + ' ' + apiPath;
        const err = new Error(message);
        err.name = 'HttpError';

        // Optional correlation id for debugging: backend may set one of these headers.
        // We keep only the id value (no payload/body).
        const contextRequestId =
            response.headers?.get('x-request-id') ||
            response.headers?.get('x-correlation-id') ||
            response.headers?.get('x-correlationID') ||
            response.headers?.get('x-requestid');

        this.sendException(err, {
            source: 'http',
            http_status: status,
            http_method: method,
            api_path: apiPath,
            ...(contextRequestId ? { context_request_id: contextRequestId } : {}),
        });
    }

    private init(): void {
        if (!this.config?.apiKey) {
            return;
        }
        if (this.config.deployEnv === 'local' || this.config.deployEnv === 'dev') {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            posthog.init(this.config.apiKey, {
                api_host: this.config.apiHost,
                capture_pageview: false,
                capture_pageleave: false,
                autocapture: false,
                disable_session_recording: true,
                person_profiles: 'identified_only',
                before_send: (event: any) => {
                    if (event?.event === '$pageview' || event?.event === '$pageleave') {
                        return null;
                    }
                    if (!event?.properties) {
                        return event;
                    }

                    const props = event.properties as Record<string, any>;

                    // Remove GeoIP/location enrichment (GDPR/privacy minimization)
                    for (const key of Object.keys(props)) {
                        if (key.startsWith('$geoip_')) {
                            delete props[key];
                        }
                    }

                    // Remove raw IP (if present) and raw UA
                    delete props.$ip;
                    delete props.$raw_user_agent;

                    // Avoid sending full URLs / paths / referrers (we already send a sanitized `path`)
                    delete props.$current_url;
                    delete props.$initial_current_url;
                    delete props.$session_entry_url;
                    delete props.$pathname;
                    delete props.$referrer;
                    delete props.$referring_domain;

                    // Avoid exact screen dimensions unless explicitly needed for UI debugging
                    delete props.$screen_width;
                    delete props.$screen_height;

                    // Drop marketing click identifiers if they ever appear
                    for (const key of Object.keys(props)) {
                        const k = key.toLowerCase();
                        if (
                            k.includes('utm_') ||
                            k.includes('gclid') ||
                            k.includes('fbclid') ||
                            k.includes('msclkid') ||
                            k.includes('ttclid') ||
                            k.includes('wbraid') ||
                            k.includes('gbraid')
                        ) {
                            delete props[key];
                        }
                    }

                    return event;
                },
            });
        });
        this.started = true;

        this.registerContext();
        this.identifyFromSession(this.localStorageService.getCurrentUser());

        this.localStorageService.watchCurrentUser().subscribe(session => {
            this.identifyFromSession(session);
        });
        this.localStorageService.watchChangeCurrentMandant().subscribe(() => {
            this.registerContext();
        });
        this.localStorageService.watchLoggedIn().subscribe(loggedIn => {
            if (!loggedIn) {
                this.reset();
            }
        });
    }

    private identifyFromSession(session: KieneSession | null): void {
        if (!this.started) {
            return;
        }
        if (!session?.benutzer_id) {
            return;
        }
        const distinctId = 'user_' + session.benutzer_id;
        if (this.lastDistinctId && this.lastDistinctId !== distinctId) {
            this.reset();
        }
        this.lastDistinctId = distinctId;
        this.ngZone.runOutsideAngular(() => {
            posthog.identify(distinctId, {
                benutzer_id: session.benutzer_id,
                app: this.config.app,
                deploy_env: this.config.deployEnv,
                tierarzt_id: session.tierarzt_id || 0,
            });
        });
        this.registerContext();
    }

    private registerContext(): void {
        if (!this.started) {
            return;
        }
        const mandantId = this.currentMandantId();
        const benutzerId = this.currentBenutzerId();
        this.ngZone.runOutsideAngular(() => {
            posthog.register({
                app: this.config.app,
                deploy_env: this.config.deployEnv,
                mandant_id: mandantId,
                benutzer_id: benutzerId,
                client: this.clientPlatform(),
            });
        });
    }

    private reset(): void {
        if (!this.started) {
            return;
        }
        this.lastDistinctId = null;
        this.ngZone.runOutsideAngular(() => {
            posthog.reset();
        });
        this.registerContext();
    }

    private currentBenutzerId(): number {
        try {
            return this.localStorageService.getCurrentUser()?.benutzer_id || 0;
        } catch {
            return 0;
        }
    }

    private currentMandantId(): number {
        try {
            return this.localStorageService.getCurrentMandant()?.mandant_id || 0;
        } catch {
            return 0;
        }
    }

    private sendException(err: Error, extra?: Record<string, string | number | boolean>): void {
        const path = Hilfe.urlAufbereiten(this.router.url || '/');
        const frames = this.parseStackFrames(err);
        const topFrame = frames[0];
        const properties = this.withApp({
            $exception_message: err.message || 'Unknown error',
            $exception_type: err.name || 'Error',
            $exception_level: 'error',
            $exception_list: [
                {
                    type: err.name || 'Error',
                    value: err.message || 'Unknown error',
                    mechanism: { handled: true, type: 'generic' },
                    stacktrace: frames.length
                        ? { type: 'raw', frames }
                        : undefined,
                },
            ],
            path,
            ...this.exceptionContext(),
            context_stack_top_function: topFrame?.function || '',
            context_stack_top_file: topFrame?.filename || '',
            ...(extra || {}),
        });
        this.ngZone.runOutsideAngular(() => {
            posthog.capture('$exception', properties);
        });
    }

    private exceptionContext(): Record<string, number | string> {
        const session = this.localStorageService.getCurrentUser();
        return {
            benutzer_id: Number(session?.benutzer_id || 0),
            tierarzt_id: Number(session?.tierarzt_id || 0),
            mandant_id: this.currentMandantId(),
            release: this.config?.release || 'unknown',
        };
    }

    /**
     * posthog-js 1.260 has no captureException(); Error Tracking only shows a stack
     * if $exception_list[0].stacktrace.frames is present.
     */
    private parseStackFrames(err: Error): Array<{
        filename: string;
        function: string;
        lineno: number;
        colno: number;
        in_app: boolean;
    }> {
        const stack = err.stack;
        if (!stack) {
            return [];
        }
        const frames: Array<{
            filename: string;
            function: string;
            lineno: number;
            colno: number;
            in_app: boolean;
        }> = [];
        for (const raw of stack.split('\n')) {
            const line = raw.trim();
            if (!line || line === err.name || line.indexOf(err.name + ':') === 0) {
                continue;
            }
            let fn = '?';
            let loc = line;
            if (line.indexOf('at ') === 0) {
                loc = line.slice(3);
                const paren = loc.indexOf(' (');
                if (paren !== -1 && loc.charAt(loc.length - 1) === ')') {
                    fn = loc.slice(0, paren);
                    loc = loc.slice(paren + 2, -1);
                }
            } else {
                const at = line.lastIndexOf('@');
                if (at !== -1) {
                    fn = line.slice(0, at) || '?';
                    loc = line.slice(at + 1);
                }
            }
            const match = loc.match(/^(.*):(\d+):(\d+)$/);
            if (!match) {
                continue;
            }
            frames.push({
                filename: match[1],
                function: fn,
                lineno: Number(match[2]),
                colno: Number(match[3]),
                in_app: true,
            });
        }
        return frames.reverse();
    }

    private unwrapError(error: unknown): Error | HttpErrorResponse {
        let current: any = error;
        const seen = new Set();
        while (current && typeof current === 'object' && !seen.has(current)) {
            seen.add(current);
            if (current.rejection) {
                current = current.rejection;
                continue;
            }
            if (current.ngOriginalError) {
                current = current.ngOriginalError;
                continue;
            }
            if (current.originalError) {
                current = current.originalError;
                continue;
            }
            break;
        }
        if (current instanceof HttpErrorResponse) {
            return current;
        }
        if (current instanceof Error) {
            return current;
        }
        return new Error(typeof current === 'string' ? current : 'Unknown error');
    }

    private shouldIgnoreMessage(message: string): boolean {
        if (!message) {
            return false;
        }
        return (
            message.indexOf('ExpressionChangedAfterItHasBeenCheckedError') >= 0 ||
            message.indexOf('0 Unknown Error') >= 0
        );
    }

    private sanitizeUrl(url: string): string {
        try {
            const parsed = new URL(url, window.location.origin);
            return Hilfe.urlAufbereiten(parsed.pathname);
        } catch {
            return Hilfe.urlAufbereiten(url || '');
        }
    }

    private withApp(properties?: Record<string, unknown>): Record<string, unknown> {
        return {
            app: this.config.app,
            deploy_env: this.config.deployEnv,
            client: this.clientPlatform(),
            benutzer_id: this.currentBenutzerId(),
            release: this.config.release || 'unknown',
            ...(properties || {}),
        };
    }

    private clientPlatform(): string {
        const platform = getLegacyWebViewPlatform();
        if (platform === 'ios') {
            return 'webview_ios';
        }
        if (platform === 'android') {
            return 'webview_android';
        }
        return 'browser';
    }
}
